import { BACKEND_URL } from '../config';
import { buildGameHistoryFromPartie, type ParsedPartie } from './differeReplay';
import {
  gameHistoryToTrainingGameData,
  type CompareReplayDealEntry,
} from './competitionReplay';
import type { TrainingHistoryLoadRequest, TrainingHistoryContext } from './trainingHistory';

export function resolveLoadRequestDiffereContext(
  loadRequest: TrainingHistoryLoadRequest,
  context?: TrainingHistoryContext,
): TrainingHistoryContext['differe'] | undefined {
  return context?.differe ?? loadRequest.historyContext?.differe;
}

export const DIFFERE_SUB_TYPE_LABELS: Record<string, string> = {
  mixte: 'Mixte',
  normal: 'Normal',
  maxi: 'Maxi',
  attaque: 'Attaque',
  defense: 'Défense',
  attaqueSC: 'Attaque SC',
  maxiAtt: 'Maxi Attaque',
  maxiDef: 'Maxi Défense',
};

export function differeTypeLabel(diffType: string): string {
  switch (diffType) {
    case 'indiv': return 'Individuel';
    case 'tripl': return 'Triplette';
    case 'quad': return 'Équipe';
    default: return diffType;
  }
}

export function resolveDiffereBatchTitle(
  diffType: string,
  subType: string | undefined,
  playerLabel: string,
): string {
  const typeDisplay = subType
    ? (DIFFERE_SUB_TYPE_LABELS[subType] || subType)
    : differeTypeLabel(diffType);
  return `${typeDisplay} — ${playerLabel}`;
}

export interface DiffereDonneItem {
  id: number;
  numdonne: number;
}

export interface DiffereCompareReplayBatch {
  title: string;
  deals: CompareReplayDealEntry[];
  selectedIndex: number;
  selectedVariantIndex: number;
}

function dealHasReplayPayload(deal: CompareReplayDealEntry): boolean {
  if (deal.variants?.length) {
    return deal.variants.some((v) => !!v.gameData);
  }
  return !!deal.gameData;
}

/** True when a différé session should reload all player deals (missing or incomplete batch). */
export function differeCompareReplayBatchNeedsRebuild(
  loadRequest: TrainingHistoryLoadRequest,
  context?: TrainingHistoryContext,
): boolean {
  const d = resolveLoadRequestDiffereContext(loadRequest, context);
  if (d?.differeId == null || !d.differeType || !d.pseudo) return false;

  const batch = loadRequest.compareReplayBatch;
  if (!batch?.deals?.length || batch.deals.length <= 1) return true;

  return batch.deals.some((deal) => !dealHasReplayPayload(deal as CompareReplayDealEntry));
}

export async function fetchDifferePlayerDonnes(
  diffType: string,
  differeId: number,
  playerPseudo: string,
): Promise<DiffereDonneItem[]> {
  const params = new URLSearchParams({ player: playerPseudo });
  const res = await fetch(
    `${BACKEND_URL}/api/competition/differe/${diffType}/${differeId}/donnes?${params}`,
  );
  const json = await res.json();
  if (!json.success) throw new Error(json.error || `Erreur ${res.status}`);
  return (json.data?.donnes ?? []) as DiffereDonneItem[];
}

export async function buildDiffereCompareReplayBatch(params: {
  diffType: string;
  differeId: number;
  playerPseudo: string;
  playerLabel: string;
  typeLabel: string;
  selectedDealNum?: number;
  selectedPartieId?: number;
  donnes?: DiffereDonneItem[];
}): Promise<DiffereCompareReplayBatch> {
  const donnes = params.donnes?.length
    ? params.donnes
    : await fetchDifferePlayerDonnes(params.diffType, params.differeId, params.playerPseudo);

  if (donnes.length === 0) {
    throw new Error('Aucune donne pour ce joueur');
  }

  const dealsOut: CompareReplayDealEntry[] = await Promise.all(
    donnes.map(async (donne) => {
      const res = await fetch(
        `${BACKEND_URL}/api/competition/differe/${params.diffType}/replay/${donne.id}`,
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur inconnue');
      const partie = json.data?.partie as ParsedPartie;
      if (!partie) throw new Error(`Donne ${donne.numdonne} : replay indisponible`);

      const history = buildGameHistoryFromPartie(partie);
      const gameData = gameHistoryToTrainingGameData(history);
      const replaySeatLabels = [...history.players]
        .sort((a, b) => a.position - b.position)
        .map((p) => p.name);

      return {
        dealIndex: Number(donne.numdonne),
        label: `Donne ${donne.numdonne}`,
        gameData,
        replaySeatLabels,
        partieId: Number(donne.id),
      };
    }),
  );

  dealsOut.sort((a, b) => a.dealIndex - b.dealIndex);

  let selectedIndex = 0;
  if (params.selectedDealNum != null) {
    const idx = dealsOut.findIndex((d) => d.dealIndex === params.selectedDealNum);
    if (idx >= 0) selectedIndex = idx;
  } else if (params.selectedPartieId != null) {
    const idx = dealsOut.findIndex((d) => d.partieId === params.selectedPartieId);
    if (idx >= 0) selectedIndex = idx;
  }

  return {
    title: params.typeLabel,
    deals: dealsOut,
    selectedIndex,
    selectedVariantIndex: 0,
  };
}
